"use client";
import { useAppSelector } from "@/store/hooks";
import { sortDataType } from "@/store/types/aiCourseFinder";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useState } from "react";
import NoRecordsFound from "../common/ai-course-finder/NoRecordsFound";
import CountryUniCard from "../common/CountryUniCard";
import { Dropdown } from "../common/Dropdown";
import { UniversitiesSortOptions } from "../common/Helper";
import { Search, SortUpArrow } from "../common/Icons";
import { Skeleton } from "../ui/skeleton";

interface AllUniversityProps {
  country?: string | null;
  forceLoading?: boolean;
  selectedCategory: string;
  setSelectedCategory: React.Dispatch<React.SetStateAction<string>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setSortData: React.Dispatch<React.SetStateAction<sortDataType>>;
  sortData: sortDataType;
  onSearch: (value: string) => void;
}

const UniversityCardSkeleton = ({
  isTouchDevice,
}: {
  isTouchDevice: boolean;
}) => (
  <div
    className={`overflow-hidden rounded-[22px] shadow-[0px_4px_12px_0px_#3777FF26] max-w-[400px] mx-auto md:max-w-full w-full bg-white ${isTouchDevice
      ? "h-[480px] sm:max-h-[580px] xl:max-h-[700px] sm:h-full"
      : "h-[570px] xl:h-[350px] 2xl:h-[400px] 3xl:h-[450px]"
      }`}
  >
    <Skeleton className="w-full h-[42%] rounded-none" />
    <div className="p-4 sm:p-5 space-y-3">
      <Skeleton className="h-6 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-[85%]" />
      <div className="grid grid-cols-2 gap-3 pt-2">
        <Skeleton className="h-12 w-full rounded-lg" />
        <Skeleton className="h-12 w-full rounded-lg" />
      </div>
      <Skeleton className="h-10 w-full rounded-xl mt-4" />
    </div>
  </div>
);

const AllUniversities: React.FC<AllUniversityProps> = ({
  selectedCategory,
  setSelectedCategory,
  setSortData,
  setPage,
  sortData,
  country,
  forceLoading = false,
  onSearch,
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const filter = searchParams.get("f");
  const [selectedSort, setSelectedSort] = useState<string | null>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isTransitionLoading, setIsTransitionLoading] = useState(false);
  const [localSearch, setLocalSearch] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      onSearch(localSearch);
    }, 500);
    return () => clearTimeout(handler);
  }, [localSearch, onSearch]);

  useEffect(() => {
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    setIsTouchDevice(isTouch);
    setSelectedCategory(filter || "All");
  }, [filter]);

  const {
    loading,
    universityList,
    universityListTotal,
    universityFilterOptions,
  } = useAppSelector((state) => state.country);

  useEffect(() => {
    if (!loading) {
      setIsTransitionLoading(false);
    }
  }, [loading]);
  const isLoadingState = forceLoading || loading || isTransitionLoading;

  return (
    <div className="container 2xl:max-w-[1628px] mt-5 sm:mt-[10px] relative z-10 pb-5 md:pb-20">
      <div className="border-b-2 border-black pb-5 flex items-center gap-2 sm:gap-4 flex-wrap">
        <button
          onClick={() => {
            setIsTransitionLoading(true);
            setPage(1);
            setSelectedCategory("All");
            router.push(
              `/all-universities/${country?.replaceAll("europe -", "").toLocaleLowerCase().replaceAll(" ", "-").replaceAll("united-states-of-america", "united-states")}?f=All`,
            );
          }}
          className={`py-[10px] px-[20px] cursor-pointer rounded-[42px] ff_poppins text-black font-semibold duration-300 ease-in-out text-sm sm:text-base lg:text-lg xl:text-xl ${selectedCategory === "All" ? "bg-[#5E91FF66]" : "bg-[#F3F4F6]"
            }`}
        >
          All
        </button>
        {universityFilterOptions &&
          universityFilterOptions.map((course) => (
            <button
              key={course.course_level}
              onClick={() => {
                setIsTransitionLoading(true);
                setPage(1);
                setSelectedCategory(course.course_level);
                router.push(
                  `/all-universities/${country?.replaceAll("europe -", "").toLocaleLowerCase().replaceAll(" ", "-").replaceAll("united-states-of-america", "united-states")}?f=${course.course_level}`,
                );
              }}
              className={`py-[10px] px-[20px] cursor-pointer rounded-[42px] ff_poppins text-black font-semibold duration-300 ease-in-out text-sm sm:text-base lg:text-lg xl:text-xl ${selectedCategory === course.course_level
                ? "bg-[#5E91FF66]"
                : "bg-[#F3F4F6]"
                }`}
            >
              {course.course_level}
            </button>
          ))}
      </div>

      <div className="flex sm:items-center justify-between gap-2 flex-col sm:flex-row mt-5">
        <div className="flex items-center gap-3 sm:gap-4 xl:gap-8 w-full sm:w-auto flex-row">
          <div className="flex items-center gap-1 cursor-pointer">
            <Dropdown
              titleClass="hidden sm:block ff_poppins font-semibold text-base sm:text-lg xl:text-xl leading-[30px] text-black"
              icon={<SortUpArrow />}
              title={selectedSort ? selectedSort : "Sort"}
              onSelect={(item) => {
                setIsTransitionLoading(true);
                if (selectedSort === item.title) {
                  setSortData({ key: "", direction: "" });
                  setSelectedSort("");
                } else {
                  setSortData({
                    ...sortData,
                    sortingKey: item.key,
                    sortingDirection: item.direction,
                  });
                  setSelectedSort(item.title);
                }
              }}
              content={UniversitiesSortOptions}
            />
          </div>
          <div className="relative w-full sm:w-64 md:w-80">
            <input
              type="text"
              placeholder="Search universities..."
              value={localSearch}
              onChange={(e) => {
                setLocalSearch(e.target.value);
              }}
              className="w-full pl-10 pr-8 py-2 border border-slate-200 rounded-[18px] text-sm focus:outline-none focus:border-indigo-500 bg-white text-slate-800 shadow-sm ff_poppins"
            />
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Search color="#94a3b8" width={16} height={16} />
            </div>
            {localSearch && (
              <button
                onClick={() => {
                  setLocalSearch("");
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
              >
                ✕
              </button>
            )}
          </div>
        </div>
        {isLoadingState ? (
          <p className="text-sm sm:text-base md:text-lg xl:text-xl leading-[30px] ff_poppins">
            Loading results...
          </p>
        ) : (
          <p className="text-sm sm:text-base md:text-lg xl:text-xl leading-[30px] ff_poppins">
            Showing {universityList?.length || 0} out of {universityListTotal}{" "}
            results
          </p>
        )}
      </div>
      {isLoadingState ? (
        <div className="mt-[27px] grid grid-cols-1 md:grid-cols-2 items-center gap-5 xl:gap-[32px] 3xl:gap-y-[64px] 3xl:gap-x-[96px]">
          {Array.from({ length: 6 }).map((_, idx) => (
            <UniversityCardSkeleton key={idx} isTouchDevice={isTouchDevice} />
          ))}
        </div>
      ) : universityList && universityList.length > 0 ? (
        <div className="mt-[27px] grid grid-cols-1 md:grid-cols-2 items-center gap-5 xl:gap-[32px] 3xl:gap-y-[64px] 3xl:gap-x-[96px]">
          {universityList.map((items, idx) => (
            <div
              key={idx}
              className={`overflow-hidden rounded-[22px] shadow-[0px_4px_12px_0px_#3777FF26] cursor-pointer max-w-[400px] mx-auto md:max-w-full w-full ${isTouchDevice
                ? "h-[480px] sm:max-h-[580px] xl:max-h-[700px] sm:h-full"
                : "h-[570px] xl:h-[350px] 2xl:h-[400px] 3xl:h-[450px]"
                }`}
            >
              <CountryUniCard items={items} />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex justify-center items-center py-10 min-h-[33vh]">
          <NoRecordsFound />
        </div>
      )}
    </div>
  );
};

export default AllUniversities;
